import { useCallback, useMemo, useState } from 'react'

import { DEVICES, MedconnectDevice, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'

import { buildDeviceDraft, getDeviceSettingsSpec, getMissingRequiredFields } from './device-settings-spec'

export const WIZARD_STEPS = ['connector', 'device', 'connection', 'verify'] as const
export type WizardStep = (typeof WIZARD_STEPS)[number]

export type DeviceSetupWizardState = {
  step: WizardStep
  stepIndex: number
  draft: MedconnectDeviceValue | null
  isEditing: boolean
  isSaving: boolean
  savedDeviceName: DEVICES | null
  canContinue: boolean
  missingFields: string[]
  chooseDevice: (device: DEVICES) => void
  updateDraft: (patch: Partial<MedconnectDeviceValue>) => void
  goBack: () => void
  goNext: () => void
  /** Persist the draft through `onSave`; on success the wizard moves to verification. */
  save: () => Promise<void>
}

type Params = {
  initialDevice?: MedconnectDevice
  connectorReachable: boolean
  onSave: (value: MedconnectDeviceValue) => Promise<boolean>
}

/** State machine for the guided device setup; keeps components presentational. */
export function useDeviceSetupWizard({ initialDevice, connectorReachable, onSave }: Params): DeviceSetupWizardState {
  const isEditing = !!initialDevice
  const [stepIndex, setStepIndex] = useState(isEditing ? 2 : 0)
  const [draft, setDraft] = useState<MedconnectDeviceValue | null>(() => (initialDevice ? stripId(initialDevice) : null))
  const [isSaving, setIsSaving] = useState(false)
  const [savedDeviceName, setSavedDeviceName] = useState<DEVICES | null>(null)

  const step = WIZARD_STEPS[stepIndex]
  const missingFields = useMemo(() => (draft ? getMissingRequiredFields(draft) : []), [draft])

  const canContinue = useMemo(() => {
    if (step === 'connector') return connectorReachable
    if (step === 'device') return draft !== null
    if (step === 'connection') return missingFields.length === 0
    return true
  }, [connectorReachable, draft, missingFields, step])

  const chooseDevice = useCallback((device: DEVICES) => {
    setDraft((current) => (current?.name === device ? current : buildDeviceDraft(device)))
  }, [])

  const updateDraft = useCallback((patch: Partial<MedconnectDeviceValue>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current))
  }, [])

  const goBack = useCallback(() => setStepIndex((index) => Math.max(0, index - 1)), [])
  const goNext = useCallback(() => setStepIndex((index) => Math.min(WIZARD_STEPS.length - 1, index + 1)), [])

  const save = useCallback(async () => {
    if (!draft || missingFields.length > 0) return
    setIsSaving(true)
    try {
      const saved = await onSave(normalizeDraft(draft))
      if (!saved) return
      setSavedDeviceName(draft.name)
      setStepIndex(WIZARD_STEPS.indexOf('verify'))
    } finally {
      setIsSaving(false)
    }
  }, [draft, missingFields.length, onSave])

  return { step, stepIndex, draft, isEditing, isSaving, savedDeviceName, canContinue, missingFields, chooseDevice, updateDraft, goBack, goNext, save }
}

function stripId(device: MedconnectDevice): MedconnectDeviceValue {
  const { _id, ...value } = device
  return value
}

/** Drop empty strings so the connector stores only what was actually set. */
function normalizeDraft(draft: MedconnectDeviceValue): MedconnectDeviceValue {
  const spec = getDeviceSettingsSpec(draft.name)
  const normalized: MedconnectDeviceValue = { name: draft.name, type: spec.type }
  for (const [key, value] of Object.entries(draft)) {
    if (key === 'name' || key === 'type') continue
    if (value === '' || value === undefined || value === null) continue
    Object.assign(normalized, { [key]: value })
  }
  return normalized
}
